import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { useAuthStore } from './stores/authStore';
import { useAuthInit } from './hooks/useAuthInit';
import { ROLES, isAdminRole } from './constants/roles';
import { Spinner, UiToastProvider, useUiToast } from './components/ui';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthGround } from './components/auth/AuthLayout';
import { homePathForRole, LEGACY_DASHBOARD } from './components/shell/navItems';
// 設計トークンと、複数の遅延 chunk から読まれるスタイルはここで一度だけ固定順に読み込む。
// 同じ CSS を別々の chunk が別の順序で読むと webpack が "Conflicting order" を出し、
// CI ではそれが error になる（settings.css は設定ページと通知プロンプトの両方が読む、
// routeMap.css はキャスト / ドライバー / 管理の 3 chunk が読む）。
import './styles/tokens.css';
import './components/shell/shell.css';
import './components/PullToRefreshIndicator.css';
import './components/settings/settings.css';
import './components/routeMap.css';

// Eagerly loaded (landing + auth pages - needed immediately)
import HomePage from './components/HomePage';
import Login from './components/Login';

// Lazily loaded (role-specific dashboards - loaded on demand)
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const CastDashboard = lazy(() => import('./components/CastDashboard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const DriverDashboard = lazy(() => import('./components/DriverDashboard'));
const SystemDashboard = lazy(() => import('./components/SystemDashboard'));

// UI 刷新 Phase 1 の部品カタログ。業務画面からは参照されないので、
// REACT_APP_UI_PREVIEW=1 のときだけ配線する（lazy なので、その 1 行が
// 通らない限りチャンクは読み込まれない）
const UiPreview = lazy(() => import('./pages/UiPreview'));

interface PushNotificationMessage {
  type: 'PUSH_NOTIFICATION_RECEIVED';
  notification: {
    title?: string;
    body?: string;
  };
}

function isPushNotificationMessage(data: unknown): data is PushNotificationMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === 'PUSH_NOTIFICATION_RECEIVED'
  );
}

function LoadingFallback() {
  return (
    <AuthGround>
      <Spinner size="lg" primary label="読み込み中" />
    </AuthGround>
  );
}

function AppContent() {
  useAuthInit();
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const logout = useAuthStore((s) => s.logout);
  const toast = useUiToast();

  // Listen for push notifications from Service Worker
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (isPushNotificationMessage(event.data)) {
        const { notification } = event.data;
        // Show toast notification with push notification content
        const message = notification.title && notification.body
          ? `${notification.title}\n${notification.body}`
          : notification.body || notification.title || '新しい通知があります';

        toast.show({ message, tone: 'info' });
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, [toast]);

  if (loading) {
    return <LoadingFallback />;
  }

  // Where a signed-in user lands: /admin, /system, /cast/request or
  // /driver/shift. Unsigned users go to /login.
  const home = user ? homePathForRole(user.role) : '/login';
  const goHome = <Navigate to={home} replace />;

  // The old single /dashboard URL: every known role is redirected to its own
  // area; only an unknown role has nowhere to go and stays here.
  const renderLegacyDashboard = () => {
    if (!user) return goHome;
    if (home === LEGACY_DASHBOARD) return <div>Unknown role: {user.role}</div>;
    return goHome;
  };

  // Role guard: a cast opening /admin (or a shop admin opening /system, or a
  // driver opening /cast) is sent to their own home instead of seeing a blank
  // shell.
  const renderCastArea = () => {
    if (!user) return goHome;
    if (user.role !== ROLES.CAST) return goHome;
    return <CastDashboard user={user} onLogout={logout} />;
  };

  const renderDriverArea = () => {
    if (!user) return goHome;
    if (user.role !== ROLES.DRIVER) return goHome;
    return <DriverDashboard user={user} onLogout={logout} />;
  };

  const renderAdminArea = () => {
    if (!user) return goHome;
    if (!isAdminRole(user.role)) return goHome;
    return <AdminDashboard user={user} onLogout={logout} />;
  };

  const renderSystemArea = () => {
    if (!user) return goHome;
    if (user.role !== ROLES.SUPER_ADMIN) return goHome;
    return <SystemDashboard user={user} onLogout={logout} />;
  };

  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={user ? goHome : <HomePage />} />
          <Route path="/login" element={!user ? <Login /> : goHome} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={renderLegacyDashboard()} />
          <Route path="/cast/*" element={renderCastArea()} />
          <Route path="/driver/*" element={renderDriverArea()} />
          <Route path="/admin/*" element={renderAdminArea()} />
          <Route path="/system/*" element={renderSystemArea()} />
          {process.env.REACT_APP_UI_PREVIEW === '1' && (
            <Route path="/ui-preview" element={<UiPreview />} />
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          {/* ui のトーストは全ルート共通でここに 1 度だけ張る（殻や画面側では張らない）。
              プッシュ通知のトーストもこの provider を使う */}
          <UiToastProvider>
            <AppContent />
          </UiToastProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
